import { useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { inputStyle, requiredTextStyle } from "../styles/appStyles";

// Detect env(keyboard-inset-height) support once at module load
const supportsKeyboardEnv =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("padding-bottom: env(keyboard-inset-height)");

function CreateRecipeModal({
  isOpen,
  appTheme,
  isMobile,
  // field state
  newRecipeName, setNewRecipeName,
  newRecipeImage, setNewRecipeImage,
  newRecipeCategory, setNewRecipeCategory,
  newRecipeIngredients, setNewRecipeIngredients,
  newRecipeDescription, setNewRecipeDescription,
  newRecipeTime, setNewRecipeTime,
  newRecipeCalories, setNewRecipeCalories,
  newRecipeServings, setNewRecipeServings,
  newRecipeProtein, setNewRecipeProtein,
  newRecipeCarbs, setNewRecipeCarbs,
  newRecipeFats, setNewRecipeFats,
  newRecipeTag, setNewRecipeTag,
  newRecipeInstructions, setNewRecipeInstructions,
  // section toggle state
  activeNewRecipeSections, setActiveNewRecipeSections,
  // validation
  hasTriedToSaveAddRecipe, setHasTriedToSaveAddRecipe,
  isNewRecipeNameFilled, isNewRecipeCategoryFilled,
  isNewRecipeIngredientsFilled, isNewRecipeCaloriesFilled,
  isNewRecipeProteinFilled, isNewRecipeCarbsFilled,
  isNewRecipeFatsFilled, isAddRecipeValid,
  // actions
  addRecipe, clearAddRecipeForm,
  addRecipeWarning, setAddRecipeWarning,
  setShowAddRecipeForm,
}) {
  const [ingredientInput, setIngredientInput] = useState("");
  const [ingredientQtyInput, setIngredientQtyInput] = useState("");
  const [sheetOffset, setSheetOffset] = useState(0);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Keyboard avoidance — visualViewport fallback
  useState(() => {
    if (!isOpen || supportsKeyboardEnv) return;
    function handleViewport() {
      const vv = window.visualViewport;
      if (!vv) return;
      setSheetOffset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    }
    window.visualViewport?.addEventListener("resize", handleViewport);
    window.visualViewport?.addEventListener("scroll", handleViewport);
    return () => {
      window.visualViewport?.removeEventListener("resize", handleViewport);
      window.visualViewport?.removeEventListener("scroll", handleViewport);
      setSheetOffset(0);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const ACCENT = appTheme.accentColor;
  const isDirty = !!(
    newRecipeName.trim() ||
    newRecipeIngredients.length > 0 ||
    newRecipeCalories.trim() ||
    newRecipeProtein.trim() ||
    newRecipeCarbs.trim() ||
    newRecipeFats.trim() ||
    newRecipeDescription.trim() ||
    newRecipeTime.trim() ||
    newRecipeServings.trim() ||
    newRecipeTag ||
    newRecipeInstructions.trim() ||
    newRecipeImage
  );
  const ingredientChips = newRecipeIngredients;
  const numStyle = { ...inputStyle(appTheme), width: "100%", margin: 0, WebkitAppearance: "none", MozAppearance: "none", appearance: "none" };
  const removeBtn = { background: "none", border: "none", color: appTheme.mutedText, fontSize: "13px", fontWeight: "500", cursor: "pointer", padding: "6px 0", fontFamily: appTheme.fontBody };

  function addIngredient() {
    const name = ingredientInput.trim();
    if (!name) return;
    setNewRecipeIngredients([...ingredientChips, { name, quantity: ingredientQtyInput.trim() }]);
    setIngredientInput("");
    setIngredientQtyInput("");
  }
  function removeIngredient(idx) {
    setNewRecipeIngredients(ingredientChips.filter((_, i) => i !== idx));
  }
  function handleImageChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { if (typeof reader.result === "string") setNewRecipeImage(reader.result); };
    reader.readAsDataURL(file);
  }
  function addSection(section) {
    setActiveNewRecipeSections(prev => prev.includes(section) ? prev : [...prev, section]);
  }
  function removeSection(section) {
    setActiveNewRecipeSections(prev => prev.filter(s => s !== section));
    if (section === "photo")        setNewRecipeImage("");
    if (section === "description")  setNewRecipeDescription("");
    if (section === "time")         setNewRecipeTime("");
    if (section === "servings")     setNewRecipeServings("");
    if (section === "tag")          setNewRecipeTag("");
    if (section === "instructions") setNewRecipeInstructions("");
  }
  function handleClose() {
    if (isDirty) { setShowDiscardDialog(true); return; }
    clearAddRecipeForm();
    setShowAddRecipeForm(false);
  }
  function discardAndClose() {
    setShowDiscardDialog(false);
    clearAddRecipeForm();
    setShowAddRecipeForm(false);
  }

  const sheetStyle = {
    position: "fixed", bottom: 0, left: 0, right: 0,
    zIndex: 2001,
    maxWidth: "560px", margin: "0 auto",
    height: "95vh",
    borderRadius: "24px 24px 0 0",
    background: appTheme.pageBackground,
    boxShadow: "0 -8px 40px rgba(15,23,42,0.14)",
    animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
    display: "flex", flexDirection: "column",
    willChange: "transform",
    ...(supportsKeyboardEnv
      ? { paddingBottom: "env(keyboard-inset-height, 0px)" }
      : { transform: `translateY(-${sheetOffset}px)`, transition: sheetOffset > 0 ? "transform 0.15s ease" : "none" }
    ),
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={handleClose} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }} />

      {/* Sheet */}
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "10px 18px 12px" : "12px 24px 14px", flexShrink: 0 }}>
          <button onClick={handleClose} style={{ position: "absolute", right: isMobile ? "12px" : "16px", top: "50%", transform: "translateY(-50%)", width: "32px", height: "32px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke={appTheme.secondaryText} strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
          <h2 style={{ margin: 0, fontSize: isMobile ? "20px" : "24px", fontWeight: "600", color: appTheme.primaryText, fontFamily: appTheme.fontHeading, lineHeight: 1.1, textAlign: "center" }}>
            Add Your Own Recipe
          </h2>
        </div>

        {/* Scrollable content */}
        <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ display: "grid", gap: "12px", padding: isMobile ? "4px 18px 24px" : "4px 24px 24px" }}>

            <div style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", color: appTheme.secondaryText, fontFamily: appTheme.fontBody }}>BASICS</div>

            {/* Recipe name + meal type */}
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <div style={{ flex: "0 0 60%" }}>
                <input value={newRecipeName} onChange={e => setNewRecipeName(e.target.value)} placeholder="Recipe name" style={{ ...inputStyle(appTheme), width: "100%", margin: 0 }} />
                {hasTriedToSaveAddRecipe && !isNewRecipeNameFilled && <div style={requiredTextStyle(appTheme)}>* required</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <select value={newRecipeCategory} onChange={e => setNewRecipeCategory(e.target.value)} style={{ ...inputStyle(appTheme), width: "100%", margin: 0 }}>
                  <option value="">Meal type</option>
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                </select>
                {hasTriedToSaveAddRecipe && !isNewRecipeCategoryFilled && <div style={requiredTextStyle(appTheme)}>* required</div>}
              </div>
            </div>

            {/* Ingredient tag input */}
            <div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input value={ingredientQtyInput} onChange={e => setIngredientQtyInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addIngredient(); } }} placeholder="Qty" style={{ ...inputStyle(appTheme), flex: "0 0 35%", width: "35%", margin: 0 }} />
                <input value={ingredientInput} onChange={e => setIngredientInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addIngredient(); } }} placeholder="Ingredient" style={{ ...inputStyle(appTheme), flex: 1, width: "100%", margin: 0 }} />
                <button type="button" onClick={addIngredient} disabled={!ingredientInput.trim()} style={{ width: "44px", height: "44px", borderRadius: "999px", border: "none", background: ingredientInput.trim() ? ACCENT : appTheme.pillBackground, color: ingredientInput.trim() ? "#fff" : appTheme.mutedText, cursor: ingredientInput.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s ease", opacity: ingredientInput.trim() ? 1 : 0.45 }}>
                  <Plus size={18} weight="bold" />
                </button>
              </div>
              {ingredientChips.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
                  {ingredientChips.map((chip, idx) => (
                    <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: appTheme.accentLight, border: appTheme.accentBorder, color: ACCENT, padding: "5px 8px 5px 12px", borderRadius: "999px", fontSize: "13px", fontWeight: "500", fontFamily: appTheme.fontBody }}>
                      {chip.quantity && <span style={{ color: appTheme.mutedText, fontWeight: "400" }}>{chip.quantity} ·{" "}</span>}
                      {chip.name}
                      <button type="button" onClick={() => removeIngredient(idx)} style={{ background: "rgba(27,67,50,0.15)", border: "none", color: ACCENT, borderRadius: "999px", width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "10px", fontWeight: "700", padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {hasTriedToSaveAddRecipe && !isNewRecipeIngredientsFilled && <div style={requiredTextStyle(appTheme)}>* required</div>}

            <div style={{ marginTop: "6px", fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", color: appTheme.secondaryText, fontFamily: appTheme.fontBody }}>NUTRITION</div>
            <div style={{ fontSize: "13px", color: appTheme.secondaryText, marginTop: "-4px", fontFamily: appTheme.fontBody }}>Needed for your daily totals in Plan.</div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <input type="number" inputMode="numeric" min="0" step="1" value={newRecipeCalories} onChange={e => setNewRecipeCalories(e.target.value)} placeholder="Calories" style={numStyle} />
                {hasTriedToSaveAddRecipe && !isNewRecipeCaloriesFilled && <div style={requiredTextStyle(appTheme)}>* required</div>}
              </div>
              <div>
                <input type="number" inputMode="numeric" min="0" step="1" value={newRecipeProtein} onChange={e => setNewRecipeProtein(e.target.value)} placeholder="Protein (g)" style={numStyle} />
                {hasTriedToSaveAddRecipe && !isNewRecipeProteinFilled && <div style={requiredTextStyle(appTheme)}>* required</div>}
              </div>
              <div>
                <input type="number" inputMode="numeric" min="0" step="1" value={newRecipeCarbs} onChange={e => setNewRecipeCarbs(e.target.value)} placeholder="Carbs (g)" style={numStyle} />
                {hasTriedToSaveAddRecipe && !isNewRecipeCarbsFilled && <div style={requiredTextStyle(appTheme)}>* required</div>}
              </div>
              <div>
                <input type="number" inputMode="numeric" min="0" step="1" value={newRecipeFats} onChange={e => setNewRecipeFats(e.target.value)} placeholder="Fats (g)" style={numStyle} />
                {hasTriedToSaveAddRecipe && !isNewRecipeFatsFilled && <div style={requiredTextStyle(appTheme)}>* required</div>}
              </div>
            </div>

            <div style={{ marginTop: "8px", fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", color: appTheme.secondaryText, fontFamily: appTheme.fontBody }}>ADD MORE</div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {[{ key: "photo", label: "+ Photo" }, { key: "description", label: "+ Description" }, { key: "time", label: "+ Time" }, { key: "servings", label: "+ Servings" }, { key: "tag", label: "+ Tag" }, { key: "instructions", label: "+ Instructions" }]
                .filter(({ key }) => !activeNewRecipeSections.includes(key))
                .map(({ key, label }) => (
                  <button key={key} type="button" onClick={() => addSection(key)} style={{ padding: "10px 14px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.accentLight, color: ACCENT, fontSize: "13px", fontWeight: "700", cursor: "pointer", fontFamily: appTheme.fontBody }}>{label}</button>
                ))}
            </div>

            {activeNewRecipeSections.includes("photo") && (
              <div style={{ marginTop: "6px", marginBottom: "6px" }}>
                <label style={{ display: "block", width: "100%", cursor: "pointer" }}>
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageChange} />
                  <div style={{ width: "100%", height: "120px", borderRadius: "16px", overflow: "hidden", border: appTheme.accentBorder, background: newRecipeImage ? appTheme.cardBackground : appTheme.accentLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {newRecipeImage ? <img src={newRecipeImage} alt="Recipe preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (
                      <div style={{ textAlign: "center", color: appTheme.secondaryText, padding: "16px" }}>
                        <div style={{ fontSize: "24px", marginBottom: "4px" }}>+</div>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: appTheme.primaryText, fontFamily: appTheme.fontBody }}>Add a photo</div>
                        <div style={{ fontSize: "12px", marginTop: "2px", fontFamily: appTheme.fontBody }}>Optional for your recipe card</div>
                      </div>
                    )}
                  </div>
                </label>
                <div style={{ display: "flex", gap: "10px", marginTop: "10px", flexWrap: "wrap", alignItems: "center" }}>
                  {newRecipeImage && <label style={{ padding: "8px 14px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.accentLight, color: ACCENT, fontWeight: "700", fontSize: "13px", cursor: "pointer", fontFamily: appTheme.fontBody }}>Change photo<input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageChange} /></label>}
                  <button type="button" onClick={() => removeSection("photo")} style={removeBtn}>{newRecipeImage ? "Remove photo" : "Remove section"}</button>
                </div>
              </div>
            )}
            {activeNewRecipeSections.includes("description") && (
              <div>
                <textarea value={newRecipeDescription} onChange={e => setNewRecipeDescription(e.target.value)} placeholder="Description" rows={4} style={{ ...inputStyle(appTheme), minHeight: "110px", resize: "vertical", fontFamily: "inherit", width: "100%", margin: 0 }} />
                <div style={{ marginTop: "8px" }}><button type="button" onClick={() => removeSection("description")} style={removeBtn}>Remove description</button></div>
              </div>
            )}
            {activeNewRecipeSections.includes("time") && (
              <div>
                <input type="number" inputMode="numeric" min="0" step="1" value={newRecipeTime} onChange={e => setNewRecipeTime(e.target.value)} placeholder="Time in minutes" style={numStyle} />
                <div style={{ marginTop: "8px" }}><button type="button" onClick={() => removeSection("time")} style={removeBtn}>Remove time</button></div>
              </div>
            )}
            {activeNewRecipeSections.includes("servings") && (
              <div>
                <input type="number" inputMode="numeric" min="0" step="1" value={newRecipeServings} onChange={e => setNewRecipeServings(e.target.value)} placeholder="Servings" style={numStyle} />
                <div style={{ marginTop: "8px" }}><button type="button" onClick={() => removeSection("servings")} style={removeBtn}>Remove servings</button></div>
              </div>
            )}
            {activeNewRecipeSections.includes("tag") && (
              <div>
                <select value={newRecipeTag} onChange={e => setNewRecipeTag(e.target.value)} style={{ ...inputStyle(appTheme), width: "100%", margin: 0 }}>
                  <option value="">Select tag</option>
                  <option value="high-protein">High Protein</option>
                  <option value="quick">Quick Meals</option>
                  <option value="under-500-kcal">Under 500 kcal</option>
                </select>
                <div style={{ marginTop: "8px" }}><button type="button" onClick={() => removeSection("tag")} style={removeBtn}>Remove tag</button></div>
              </div>
            )}
            {activeNewRecipeSections.includes("instructions") && (
              <div>
                <textarea value={newRecipeInstructions} onChange={e => setNewRecipeInstructions(e.target.value)} placeholder="Instructions" rows={6} style={{ ...inputStyle(appTheme), minHeight: "150px", resize: "vertical", fontFamily: "inherit", width: "100%", margin: 0 }} />
                <div style={{ marginTop: "8px" }}><button type="button" onClick={() => removeSection("instructions")} style={removeBtn}>Remove instructions</button></div>
              </div>
            )}

            {addRecipeWarning && (
              <div style={{ background: appTheme.actionLight, border: appTheme.actionBorder, color: appTheme.actionColor, padding: "10px 12px", borderRadius: "10px", fontSize: "14px", fontFamily: appTheme.fontBody }}>
                {addRecipeWarning}
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, padding: isMobile ? "12px 18px 20px" : "12px 24px 20px", borderTop: "1px solid rgba(0,0,0,0.06)", background: appTheme.pageBackground }}>
          <button
            onClick={() => {
              setHasTriedToSaveAddRecipe(true);
              if (!isAddRecipeValid) { setAddRecipeWarning("Whoops — I need recipe name, meal type, ingredients, calories, protein, carbs, and fats before I can save this one."); return; }
              addRecipe();
              setShowAddRecipeForm(false);
            }}
            style={{ width: "100%", padding: "14px", borderRadius: "14px", fontSize: "15px", fontWeight: "700", border: "none", background: isAddRecipeValid ? ACCENT : appTheme.pillBackground, color: isAddRecipeValid ? "#ffffff" : appTheme.secondaryText, cursor: isAddRecipeValid ? "pointer" : "not-allowed", opacity: isAddRecipeValid ? 1 : 0.72, fontFamily: appTheme.fontBody }}
          >
            Save Recipe
          </button>
        </div>

      </div>

      {/* Discard confirmation dialog */}
      {showDiscardDialog && (
        <div
          onClick={() => setShowDiscardDialog(false)}
          style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: appTheme.cardBackground, borderRadius: "20px", padding: "28px 24px 24px", boxShadow: "0 8px 40px rgba(15,23,42,0.18)", width: "100%", maxWidth: "320px" }}>
            <div style={{ fontFamily: appTheme.fontHeading, fontSize: "20px", fontWeight: "600", color: appTheme.primaryText, marginBottom: "8px" }}>Discard recipe?</div>
            <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.5, marginBottom: "24px", fontFamily: appTheme.fontBody }}>This recipe won't be saved.</div>
            <div style={{ display: "grid", gap: "10px" }}>
              <button onClick={() => setShowDiscardDialog(false)} style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "none", background: appTheme.accentColor, color: "#ffffff", fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "700", cursor: "pointer" }}>Keep Editing</button>
              <button onClick={discardAndClose} style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "none", background: "none", color: appTheme.actionColor, fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "600", cursor: "pointer" }}>Discard</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CreateRecipeModal;
